import React, { useState, useCallback, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { Box, Chip } from "@mui/material";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import { green } from "@material-ui/core/colors";
import {
  Button,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Table,
  TableHead,
  Paper,
  Tooltip,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import {
  Edit,
  SignalCellularConnectedNoInternet2Bar,
  SignalCellularConnectedNoInternet0Bar,
  SignalCellular4Bar,
  CropFree,
  Facebook,
  Instagram,
  WhatsApp,
  Settings,
} from "@material-ui/icons";

import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";

import MainContainer from "../../components/MainContainer";
import Title from "../../components/Title";
import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanies from "../../hooks/useCompanies";
import api from "../../services/api";
import WhatsAppModalCompany from "../../components/CompanyWhatsapps";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import ForbiddenPage from "../../components/ForbiddenPage";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "16px 24px",
    background: "#f5f7fa",
    height: "100%",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  header: {
    marginBottom: "16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "16px",
  },
  title: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#1a1a1a",
    margin: 0,
  },
  filterContainer: {
    background: "white",
    borderRadius: "8px",
    padding: "16px 20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    marginBottom: "16px",
  },
  filterTitle: {
    fontSize: "14px",
    fontWeight: 700,
    color: "#1a1a1a",
    marginBottom: "16px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  filterRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
    marginBottom: "16px",
  },
  filterInput: {
    padding: "10px 12px",
    border: "1px solid #e8ecf1",
    borderRadius: "6px",
    fontSize: "13px",
    fontFamily: "inherit",
    background: "#fafbfc",
    transition: "all 0.2s ease",
    "&:focus": {
      outline: "none",
      borderColor: "#3b82f6",
      background: "white",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
    },
  },
  filterLabel: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#666",
    marginBottom: "6px",
    display: "block",
  },
  filterGroup: {
    display: "flex",
    flexDirection: "column",
  },
  filterActions: {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
    alignItems: "center",
    marginTop: "16px",
    paddingTop: "16px",
    borderTop: "1px solid #e8ecf1",
    flexWrap: "wrap",
  },
  filterButtonSecondary: {
    padding: "10px 20px",
    background: "#e8ecf1",
    color: "#1a1a1a",
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": {
      background: "#d1d5db",
    },
    "&:disabled": {
      background: "#f0f0f0",
      cursor: "not-allowed",
      color: "#999",
    },
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: "12px",
    marginBottom: "16px",
  },
  statCard: {
    background: "white",
    borderRadius: "8px",
    padding: "12px 16px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    textAlign: "center",
  },
  statLabel: {
    fontSize: "12px",
    color: "#666",
    fontWeight: 500,
    marginBottom: "8px",
  },
  statValue: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#3b82f6",
  },
  tableCard: {
    background: "white",
    borderRadius: "8px",
    padding: "20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    overflow: "hidden",
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  tableContainer: {
    overflowX: "auto",
    overflowY: "auto",
    flex: 1,
    minHeight: 0,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  tableHeader: {
    background: "#f9fafb",
    borderBottom: "2px solid #e8ecf1",
    position: "sticky",
    top: 0,
    zIndex: 2,
  },
  tableHeaderCell: {
    padding: "12px",
    textAlign: "center",
    fontSize: "12px",
    fontWeight: 600,
    color: "#666",
    whiteSpace: "nowrap",
    background: "#f9fafb",
  },
  tableRow: {
    borderBottom: "1px solid #e8ecf1",
    "&:hover": {
      background: "#f9fafb",
    },
  },
  tableCell: {
    padding: "12px",
    fontSize: "13px",
    color: "#1a1a1a",
    textAlign: "center",
  },
  companyName: {
    fontWeight: 600,
    color: "#1a1a1a",
    textAlign: "center",
  },
  emptyState: {
    textAlign: "center",
    padding: "40px 20px",
    color: "#999",
  },
  tooltip: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    fontSize: theme.typography.pxToRem(14),
    border: "1px solid #dadde9",
    maxWidth: 450,
  },
  tooltipPopper: {
    textAlign: "center",
  },
  buttonProgress: {
    color: green[500],
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const CustomToolTip = ({ title, content, children }) => {
  const classes = useStyles();

  return (
    <Tooltip
      arrow
      classes={{
        tooltip: classes.tooltip,
        popper: classes.tooltipPopper,
      }}
      title={
        <React.Fragment>
          <Typography gutterBottom color="inherit">
            {title}
          </Typography>
          {content && <Typography>{content}</Typography>}
        </React.Fragment>
      }
    >
      {children}
    </Tooltip>
  );
};

const AllConnections = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { user } = useContext(AuthContext);
  const { list } = useCompanies();
  const history = useHistory();

  // State
  const [loading, setLoading] = useState(true);
  const [whats, setWhats] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [searchParam, setSearchParam] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [filterConnections, setFilterConnections] = useState([]);
  const [companyWhatsApps, setCompanyWhatsApps] = useState(null);

  const confirmationModalInitialState = {
    action: "",
    title: "",
    message: "",
    whatsAppId: "",
    open: false,
  };
  const [confirmModalInfo, setConfirmModalInfo] = useState(
    confirmationModalInitialState
  );

  // Redirect if not super
  if (!user.super) {
    history.push("/tickets");
  }

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [whatsappData, companyList] = await Promise.all([
        api.get("/whatsapp/all/?session=0").then(res => res.data),
        list()
      ]);
      setWhats(whatsappData);
      setCompanies(companyList);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  // Filter logic
  const filteredCompanies = companies.filter(company => {
    const matchesSearch = company.name.toLowerCase().includes(searchParam.toLowerCase());
    
    if (filterStatus === "all") return matchesSearch;
    
    const companyWhats = whats.filter(w => w.companyId === company.id);
    if (filterStatus === "connected") {
      return matchesSearch && companyWhats.some(w => w.status === "CONNECTED");
    }
    if (filterStatus === "disconnected") {
      return matchesSearch && companyWhats.some(w => w.status !== "CONNECTED");
    }
    return matchesSearch;
  });

  // Calculate stats
  const stats = {
    total: companies.length,
    connected: whats.filter(w => w.status === "CONNECTED").length,
    disconnected: whats.filter(w => w.status !== "CONNECTED").length,
  };

  // Handlers
  const handleOpenWhatsAppModal = (whatsappsFilter, comp) => {
    setSelectedWhatsApp(null);
    setWhatsAppModalOpen(true);
    if (whatsappsFilter?.length > 0) {
      setFilterConnections(whatsappsFilter);
      setCompanyWhatsApps(comp);
    }
  };

  const handleCloseWhatsAppModal = useCallback(() => {
    setWhatsAppModalOpen(false);
    setSelectedWhatsApp(null);
    setFilterConnections([]);
    setCompanyWhatsApps(null);
  }, []);

  const handleOpenQrModal = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setQrModalOpen(true);
  };

  const handleCloseQrModal = useCallback(() => {
    setSelectedWhatsApp(null);
    setQrModalOpen(false);
  }, []);

  const handleOpenConfirmationModal = (action, whatsAppId) => {
    if (action === "disconnect") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.disconnectTitle"),
        message: i18n.t("connections.confirmationModal.disconnectMessage"),
        whatsAppId: whatsAppId,
      });
    }

    if (action === "delete") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.deleteTitle"),
        message: i18n.t("connections.confirmationModal.deleteMessage"),
        whatsAppId: whatsAppId,
      });
    }
    setConfirmModalOpen(true);
  };

  const handleSubmitConfirmationModal = async () => {
    if (confirmModalInfo.action === "disconnect") {
      try {
        await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
      } catch (err) {
        toastError(err);
      }
    }

    if (confirmModalInfo.action === "delete") {
      try {
        await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.deleted"));
      } catch (err) {
        toastError(err);
      }
    }

    setConfirmModalInfo(confirmationModalInitialState);
  };

  const handleStartWhatsAppSession = async (whatsAppId) => {
    try {
      await api.post(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleRequestNewQrCode = async (whatsAppId) => {
    try {
      await api.put(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const responseFacebook = (response) => {
    if (response.status !== "unknown") {
      const { accessToken, id } = response;

      api
        .post("/facebook", {
          facebookUserId: id,
          facebookUserToken: accessToken,
        })
        .then((response) => {
          toast.success(i18n.t("connections.facebook.success"));
        })
        .catch((error) => {
          toastError(error);
        });
    }
  };

  const responseInstagram = (response) => {
    if (response.status !== "unknown") {
      const { accessToken, id } = response;

      api
        .post("/facebook", {
          addInstagram: true,
          facebookUserId: id,
          facebookUserToken: accessToken,
        })
        .then((response) => {
          toast.success(i18n.t("connections.facebook.success"));
        })
        .catch((error) => {
          toastError(error);
        });
    }
  };

  const renderStatusIcon = (whatsApp) => {
    return (
      <div className={classes.customTableCell}>
        {whatsApp.status === "DISCONNECTED" && (
          <CustomToolTip
            title={i18n.t("connections.toolTips.disconnected.title")}
            content={i18n.t("connections.toolTips.disconnected.content")}
          >
            <SignalCellularConnectedNoInternet0Bar color="secondary" />
          </CustomToolTip>
        )}
        {whatsApp.status === "OPENING" && (
          <CircularProgress size={24} className={classes.buttonProgress} />
        )}
        {whatsApp.status === "qrcode" && (
          <CustomToolTip
            title={i18n.t("connections.toolTips.qrcode.title")}
            content={i18n.t("connections.toolTips.qrcode.content")}
          >
            <CropFree />
          </CustomToolTip>
        )}
        {whatsApp.status === "CONNECTED" && (
          <CustomToolTip title={i18n.t("connections.toolTips.connected.title")}>
            <SignalCellular4Bar style={{ color: green[500] }} />
          </CustomToolTip>
        )}
        {(whatsApp.status === "TIMEOUT" || whatsApp.status === "PAIRING") && (
          <CustomToolTip
            title={i18n.t("connections.toolTips.timeout.title")}
            content={i18n.t("connections.toolTips.timeout.content")}
          >
            <SignalCellularConnectedNoInternet2Bar color="secondary" />
          </CustomToolTip>
        )}
      </div>
    );
  };

  const renderActionButtons = (whatsApp) => {
    return (
      <>
        {whatsApp.status === "qrcode" && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => handleOpenQrModal(whatsApp)}
          >
            {i18n.t("connections.buttons.qrcode")}
          </Button>
        )}
        {whatsApp.status === "DISCONNECTED" && (
          <>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => handleStartWhatsAppSession(whatsApp.id)}
            >
              {i18n.t("connections.buttons.tryAgain")}
            </Button>{" "}
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={() => handleRequestNewQrCode(whatsApp.id)}
            >
              {i18n.t("connections.buttons.newQr")}
            </Button>
          </>
        )}
        {(whatsApp.status === "CONNECTED" ||
          whatsApp.status === "PAIRING" ||
          whatsApp.status === "TIMEOUT") && (
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            onClick={() => {
              handleOpenConfirmationModal("disconnect", whatsApp.id);
            }}
          >
            {i18n.t("connections.buttons.disconnect")}
          </Button>
        )}
        {whatsApp.status === "OPENING" && (
          <Button size="small" variant="outlined" disabled color="default">
            {i18n.t("connections.buttons.connecting")}
          </Button>
        )}
      </>
    );
  };

  if (user.profile === "user") {
    return <ForbiddenPage />;
  }

  return (
    <MainContainer>
      <ConfirmationModal
        title={confirmModalInfo.title}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={handleSubmitConfirmationModal}
      >
        {confirmModalInfo.message}
      </ConfirmationModal>
      <QrcodeModal
        open={qrModalOpen}
        onClose={handleCloseQrModal}
        whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
      />
      <WhatsAppModalCompany
        open={whatsAppModalOpen}
        onClose={handleCloseWhatsAppModal}
        filteredWhatsapps={filterConnections}
        companyInfos={companyWhatsApps}
        whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
      />

      <div className={classes.container}>
        {/* Header */}
        <div className={classes.header}>
          <h1 className={classes.title}>🔗 Gerenciar Conexões</h1>
        </div>

        {/* Filtros */}
        <div className={classes.filterContainer}>
          <div className={classes.filterTitle}>🔍 Filtros de Busca</div>

          <div className={classes.filterRow}>
            <div className={classes.filterGroup}>
              <label className={classes.filterLabel}>Buscar Empresa</label>
              <input
                type="text"
                placeholder="Digite o nome da empresa..."
                value={searchParam}
                onChange={(e) => setSearchParam(e.target.value)}
                className={classes.filterInput}
              />
            </div>
            <div className={classes.filterGroup}>
              <label className={classes.filterLabel}>Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className={classes.filterInput}
              >
                <option value="all">Todas</option>
                <option value="connected">Com Conexões Ativas</option>
                <option value="disconnected">Com Desconexões</option>
              </select>
            </div>
          </div>

          <div className={classes.filterActions}>
            <button
              className={classes.filterButtonSecondary}
              onClick={() => {
                setSearchParam("");
                setFilterStatus("all");
              }}
              disabled={loading}
            >
              ↺ Limpar Filtros
            </button>
          </div>
        </div>

        {/* Estatísticas */}
        <div className={classes.statsGrid}>
          <div className={classes.statCard}>
            <div className={classes.statLabel}>Total de Empresas</div>
            <div className={classes.statValue}>{stats.total}</div>
          </div>
          <div className={classes.statCard}>
            <div className={classes.statLabel}>Conexões Ativas</div>
            <div className={classes.statValue} style={{ color: "#10b981" }}>
              {stats.connected}
            </div>
          </div>
          <div className={classes.statCard}>
            <div className={classes.statLabel}>Desconectadas</div>
            <div className={classes.statValue} style={{ color: "#ef4444" }}>
              {stats.disconnected}
            </div>
          </div>
        </div>

        {/* Tabela */}
        <div className={classes.tableCard}>
          {loading ? (
            <div className={classes.emptyState}>
              <CircularProgress />
            </div>
          ) : filteredCompanies.length === 0 ? (
            <div className={classes.emptyState}>
              <p>Nenhuma empresa encontrada</p>
            </div>
          ) : (
            <div className={classes.tableContainer}>
              <table className={classes.table}>
                <thead className={classes.tableHeader}>
                  <tr>
                    <th className={classes.tableHeaderCell}>Empresa</th>
                    <th className={classes.tableHeaderCell}>Ativas</th>
                    <th className={classes.tableHeaderCell}>Inativas</th>
                    <th className={classes.tableHeaderCell}>Total</th>
                    {user.profile === "admin" && (
                      <th className={classes.tableHeaderCell}>Ações</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredCompanies.map((company) => {
                    const connectedCount = whats.filter(
                      (item) =>
                        item?.companyId === company?.id &&
                        item?.status === "CONNECTED"
                    ).length;
                    const disconnectedCount = whats.filter(
                      (item) =>
                        item?.companyId === company?.id &&
                        item?.status !== "CONNECTED"
                    ).length;
                    const totalCount = whats.filter(
                      (item) => item?.companyId === company?.id
                    ).length;

                    return (
                      <tr key={company.id} className={classes.tableRow}>
                        <td className={classes.tableCell} align="center">
                          <Typography variant="body2" className={classes.companyName}>
                            {company?.name}
                          </Typography>
                        </td>
                        <td className={classes.tableCell} align="center">
                          <Chip
                            label={connectedCount}
                            size="small"
                            style={{
                              backgroundColor: "#22c55e15",
                              color: "#22c55e",
                              fontWeight: 600,
                            }}
                          />
                        </td>
                        <td className={classes.tableCell} align="center">
                          <Chip
                            label={disconnectedCount}
                            size="small"
                            style={{
                              backgroundColor: "#ef444415",
                              color: "#ef4444",
                              fontWeight: 600,
                            }}
                          />
                        </td>
                        <td className={classes.tableCell} align="center">
                          <Typography variant="body2" style={{ fontWeight: 600 }}>
                            {totalCount}
                          </Typography>
                        </td>
                        {user.profile === "admin" && (
                          <td className={classes.tableCell} align="center">
                            <Box display="flex" gap={1} justifyContent="center">
                              <Tooltip title="Editar Conexões">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    handleOpenWhatsAppModal(
                                      whats.filter(
                                        (item) =>
                                          item?.companyId === company?.id
                                      ),
                                      company
                                    );
                                  }}
                                  style={{ color: theme.palette.primary.main }}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              
                              {company.id !== 1 && (
                              <Tooltip title="Acessar como Admin">
                                <IconButton
                                  size="small"
                                  onClick={async () => {
                                    try {
                                      const { data } = await api.post("/auth/impersonate", {
                                        companyId: company.id
                                      });
                                      
                                      localStorage.setItem("token", JSON.stringify(data.token));
                                      localStorage.setItem("companyId", company.id);
                                      localStorage.setItem("userId", data.user.id);
                                      
                                      toast.success(`Acessando como admin de ${company.name}`);
                                      setTimeout(() => {
                                        window.location.href = "/";
                                      }, 1000);
                                    } catch (err) {
                                      toastError(err);
                                    }
                                  }}
                                  style={{ color: theme.palette.warning.main }}
                                >
                                  <Settings fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              )}
                            </Box>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </MainContainer>
  );
};

export default AllConnections;
